import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle, Clock3, Loader2, ShieldAlert, Ticket } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import { ticketingService } from '../services/ticketing.service'
import type { TicketTransferClaimResult } from '../types'
import { getErrorCode } from '@/lib/errors'

type ClaimState = 'choosing' | 'loading' | 'success' | 'expired' | 'invalid' | 'error'

function callableCode(error: unknown): string {
  return getErrorCode(error, 'ticketing/transfer-claim').replace(/^functions\//, '')
}

function safeManagementUrl(value: string): string | null {
  try {
    const url = new URL(value)
    if ((url.protocol !== 'https:' && url.protocol !== 'http:') || url.pathname !== '/tickets/manage') {
      return null
    }
    return url.toString()
  } catch {
    return null
  }
}

export function TicketTransferClaimPage() {
  const [searchParams] = useSearchParams()
  const transferId = searchParams.get('transferId')?.trim() || ''
  const token = searchParams.get('token')?.trim() || ''
  const { user, loading: authLoading } = useAuth()
  const [state, setState] = useState<ClaimState>('loading')
  const [result, setResult] = useState<TicketTransferClaimResult | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [continueAsGuest, setContinueAsGuest] = useState(false)
  const [claimErrorCode, setClaimErrorCode] = useState<string | null>(null)

  useEffect(() => {
    if (authLoading) return
    if (!transferId || !token) {
      setState('invalid')
      return
    }
    if (!user && !continueAsGuest) {
      setState('choosing')
      return
    }

    let active = true
    setState('loading')
    setResult(null)
    setClaimErrorCode(null)
    void ticketingService
      .claimTicketTransfer(transferId, token)
      .then((claimResult) => {
        if (!active) return
        setResult(claimResult)
        setState('success')
      })
      .catch((error: unknown) => {
        if (!active) return
        setClaimErrorCode(getErrorCode(error, 'ticketing/transfer-claim'))
        const code = callableCode(error)
        if (code === 'deadline-exceeded') {
          setState('expired')
        } else if (
          code === 'failed-precondition' ||
          code === 'permission-denied' ||
          code === 'not-found' ||
          code === 'invalid-argument'
        ) {
          setState('invalid')
        } else {
          setState('error')
        }
      })

    return () => {
      active = false
    }
  }, [attempt, authLoading, continueAsGuest, token, transferId, user])

  if (state === 'choosing') {
    const returnTo = `${window.location.pathname}${window.location.search}`
    return (
      <div className="container mx-auto max-w-lg px-4 py-16">
        <div className="space-y-6 rounded-2xl border bg-card p-6 text-center shadow-sm sm:p-8">
          <Ticket className="mx-auto h-12 w-12 text-primary" />
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Claim your ticket</h1>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Sign in first to attach the ticket to your account, or continue as a guest with this
              secure email link.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button asChild>
              <Link to="/sign-in" state={{ returnTo }}>Sign in and claim</Link>
            </Button>
            <Button type="button" variant="outline" onClick={() => setContinueAsGuest(true)}>
              Continue as guest
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (state === 'loading') {
    return (
      <div className="container mx-auto flex max-w-lg justify-center px-4 py-24">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Securing your ticket transfer…
        </div>
      </div>
    )
  }

  if (state === 'success' && result) {
    const managementUrl = safeManagementUrl(result.managementUrl)
    return (
      <div className="container mx-auto max-w-lg px-4 py-16">
        <div className="space-y-6 rounded-2xl border bg-card p-6 text-center shadow-sm sm:p-8">
          <CheckCircle className="mx-auto h-14 w-14 text-green-600" />
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Ticket claimed</h1>
            <p className="text-muted-foreground">
              This ticket now belongs to {result.currentOwnerEmail}. Its QR version has been
              refreshed, so the sender&apos;s previous ticket presentation is no longer valid.
            </p>
          </div>
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 text-left text-sm">
            <div className="flex items-start gap-3">
              <Ticket className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div>
                <p className="font-medium">Ticket reference</p>
                <p className="mt-1 font-mono text-xs text-muted-foreground">
                  {result.ticketId.slice(0, 10).toUpperCase()}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  Use the responsive ticket page or printable fallback when it is available in your
                  secure ticket-management email.
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            {managementUrl ? (
              <Button asChild>
                <a href={managementUrl} rel="noreferrer">Open secure ticket</a>
              </Button>
            ) : (
              <Button asChild>
                <Link to="/my-tickets">View my tickets</Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (state === 'expired') {
    return (
      <div className="container mx-auto max-w-lg px-4 py-16">
        <div className="space-y-5 rounded-2xl border bg-card p-6 text-center shadow-sm sm:p-8">
          <Clock3 className="mx-auto h-12 w-12 text-muted-foreground" />
          <h1 className="text-2xl font-bold">This transfer link has expired</h1>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Ask the current ticket owner to send a new secure transfer link. The ticket itself has
            not changed ownership.
          </p>
          <p className="text-xs text-muted-foreground">
            Error code: {claimErrorCode ?? 'ticketing/transfer-claim'}
          </p>
          <Button type="button" onClick={() => setAttempt((value) => value + 1)}>
            Try again
          </Button>
        </div>
      </div>
    )
  }

  const isUnavailable = state === 'invalid'
  return (
    <div className="container mx-auto max-w-lg px-4 py-16">
      <div className="space-y-5 rounded-2xl border bg-card p-6 text-center shadow-sm sm:p-8">
        <ShieldAlert className="mx-auto h-12 w-12 text-destructive" />
        <h1 className="text-2xl font-bold">
          {isUnavailable ? 'Transfer link unavailable' : 'We could not claim this ticket'}
        </h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {isUnavailable
            ? 'This secure link is invalid, has already been used, or is no longer available. Ask the sender to create a new transfer.'
            : 'Please try again. If the problem continues, ask the sender to resend the secure transfer link.'}
        </p>
        <p className="text-xs text-muted-foreground">
          Error code: {claimErrorCode ?? 'ticketing/transfer-claim'}
        </p>
        {!isUnavailable && (
          <Button type="button" onClick={() => setAttempt((value) => value + 1)}>
            Try again
          </Button>
        )}
        <p className="text-xs text-muted-foreground">
          Never share a transfer link publicly; it is the credential used to claim the ticket.
        </p>
      </div>
    </div>
  )
}
